import React, { useState, useEffect, useCallback } from "react";
import { useParams, NavLink, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import RunStatusBadge from "../components/RunStatusBadge";
import PipelineStatusAlert from "../components/Cards/PipelineStatusAlert";
import DeployKeySetup from "../components/DeployKeySetup";
import ProposalReview from "../components/ProposalReview";
import ScriptViewer from "../components/ScriptViewer";
import { Skeleton, SkeletonText } from "../../shared/ui";
import { api, getApiError } from "../../api/client";
import "../styles/golive.css";
import { POLLING_STATUSES } from "../constants";

const TABS = [
  { key: "proposal", label: "Proposal", path: (id) => `/golive/${id}` },
  { key: "script", label: "Script", path: (id) => `/golive/${id}/script` },
];

export default function RunDetailPage() {
  const { runId } = useParams();

  const location = useLocation();
  const activeTab = location.pathname.endsWith("/script")
    ? "script"
    : "proposal";

  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRun = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/golive/deployment_requests/${runId}`);
      setRun(data);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    fetchRun();
  }, [fetchRun]);

  const pollRun = useCallback(async () => {
    try {
      const { data } = await api.get(`/golive/deployment_requests/${runId}`);
      setRun(data);
    } catch (err) {
      console.error("Polling error", err);
    }
  }, [runId]);

  const runStatus = run?.status;
  useEffect(() => {
    if (!POLLING_STATUSES.has(runStatus)) return;
    const interval = setInterval(pollRun, 5000);
    return () => clearInterval(interval);
  }, [runStatus, pollRun]);

  if (loading && !run)
    return (
      <div className="gl-page">
        <Skeleton className="h-3 w-56" />
        <div className="flex flex-col gap-2">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <SkeletonText lines={8} />
      </div>
    );
  if (error)
    return (
      <div className="gl-page">
        <div className="gl-error">{error}</div>
      </div>
    );
  if (!run) return null;

  const gitRepo = run.repo_url
    .replace(/\.git$/, "")
    .split("/")
    .slice(-2)
    .join("/");

  return (
    <div className="gl-page">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-slate-400 flex-wrap">
        Deployment
        <ChevronRight size={12} />
        Run History
        <ChevronRight size={12} />
        <span className="text-slate-600 font-medium">#{run.id}</span>
      </nav>

      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="gl-h1">Run #{run.id}</h1>
          <RunStatusBadge status={run.status} />
        </div>
        <p className="mt-1 text-sm text-slate-500">
          <span className="font-mono">{gitRepo}</span>
          {" · branch "}
          <span className="font-mono">{run.git_ref}</span>
        </p>
      </div>

      {run.status === "awaiting_deploy_key" ||
      (run.is_private_repo && run.status === "failed") ? (
        <DeployKeySetup run={run} onStarted={fetchRun} />
      ) : (
        <>
          <PipelineStatusAlert
            status={run.status}
            target={run.target}
            errorMessage={(run.error_message ?? "")
              .replace(/Cloning into '[^']*'\.\.\.\s*/gi, "")
              .trim()}
            serviceCount={run.proposal?.config?.items?.length ?? 0}
          />

          <div>
            <div className="flex border-b border-gray-200 mb-5">
              {TABS.map(({ key: tabKey, label, path }) => (
                <NavLink
                  key={tabKey}
                  to={path(runId)}
                  className={[
                    "px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap no-underline",
                    "transition-colors duration-150",
                    tabKey === activeTab
                      ? "border-[var(--jade-blue-700)] text-[var(--jade-blue-700)]"
                      : "border-transparent text-slate-500 hover:text-slate-700",
                  ].join(" ")}
                >
                  {label}
                </NavLink>
              ))}
            </div>

            {activeTab === "proposal" && (
              <ProposalReview run={run} onRefresh={fetchRun} />
            )}

            {activeTab === "script" && (
              <ScriptViewer run={run} onRefresh={fetchRun} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
